// team-roles.js — Settings → Roles panel on the Team page.
//
// Phase 6.3 of the v4 multi-team daemon arc. Surfaces the role
// CRUD API shipped in PR #121 (custom-role builder). Admins can
// list, create, rename, edit the scope set, and delete custom
// roles. Builtin roles (Owner/Admin/Member/Viewer) are read-only
// for name + delete; their scope_set is also editable from this
// UI (the server enforces these constraints — this is just a UI
// affordance gate).
//
// Endpoints used:
//   GET    /v1/teams/roles            — list roles
//   POST   /v1/teams/roles            — create custom role
//   PATCH  /v1/teams/roles/:role_id   — edit name + scopes
//   DELETE /v1/teams/roles/:role_id   — delete (refuses builtins)
//
// The editor opens in a modal because the scope-checklist UX
// doesn't fit inline in a row.

import { $ } from "./dom.js";
import { escapeHTML, ago } from "./util.js";
import { apiWithMessage, AuthRedirectError } from "./api.js";
import { showConfirm, showToast } from "./ui.js";
import { showUpgradeRequiredFromError } from "./upgrade-prompt.js";
import { saveCheckoutResume } from "./checkout-resume.js";
import { teamState } from "./page-state.js";
// Cross-module cache hook (PR #133 cross-PR sweep): every role
// mutation invalidates team-bu-members.js's parallel tenantRoles
// cache so the role-picker in the workspace add-member form picks
// up new / renamed / deleted roles immediately instead of waiting
// up to 60s for that module's TTL.
import { invalidateTenantRolesCache } from "./team-bu-members.js";

// Scope vocabulary mirrors the constants in coordinator.ts. Kept
// as a flat list with display-group labels rather than imported
// from a generated config because the dashboard has no build-time
// codegen step.
//
// Drift directions and how they surface:
//   - Dashboard has a scope server doesn't know: createRole/patchRole
//     returns 400 "unknown scope" — loud, easy to debug.
//   - Server has a scope dashboard doesn't know: SILENT in the
//     naive shape — the editor would have no checkbox for it and
//     Save would drop the scope on round-trip. openRoleEditor
//     handles this case by rendering unknown scopes as a separate
//     "Unknown to this UI (preserved)" section so they ride
//     through edits unchanged.
//
// Each entry: { scope: "read:audit", label: "Read audit log",
//   group: "Audit", desc: "Optional one-liner." }
const SCOPE_VOCAB = [
  // Activity
  { scope: "read:activity",  group: "Activity", label: "Read activity", desc: "View leases, agents, sessions." },
  { scope: "write:activity", group: "Activity", label: "Write activity", desc: "Daemon push: leases, heartbeats." },
  { scope: "read:events",    group: "Activity", label: "Read events", desc: "Coordination event log." },
  { scope: "stream:events",  group: "Activity", label: "Stream events", desc: "Live SSE event stream." },
  // Policy
  { scope: "read:policy",  group: "Policy", label: "Read policy", desc: "Team policy + priority rules." },
  { scope: "admin:policy", group: "Policy", label: "Admin policy", desc: "Edit policy + priority rules." },
  // Settings
  { scope: "read:settings",  group: "Settings", label: "Read settings", desc: "Coordination settings." },
  { scope: "admin:settings", group: "Settings", label: "Admin settings", desc: "Edit coordination + workspace settings." },
  // Members + team
  { scope: "read:members",  group: "Members", label: "Read members", desc: "View team roster." },
  { scope: "admin:members", group: "Members", label: "Admin members", desc: "Invite, remove, change roles." },
  { scope: "admin:team",    group: "Members", label: "Admin team", desc: "Rename or delete team." },
  // Worktrees
  { scope: "read:worktrees",  group: "Worktrees", label: "Read worktrees", desc: "View team worktrees." },
  { scope: "write:worktrees", group: "Worktrees", label: "Write worktrees", desc: "Daemon push: worktree state." },
  // Spend + billing
  { scope: "read:spend",    group: "Billing", label: "Read spend", desc: "View LLM spend rollups." },
  { scope: "admin:spend",   group: "Billing", label: "Admin spend", desc: "Reserved; not used today." },
  { scope: "admin:billing", group: "Billing", label: "Admin billing", desc: "Stripe checkout, cancel, change plan." },
  // Webhooks
  { scope: "read:webhooks",  group: "Webhooks", label: "Read webhooks", desc: "View webhook config." },
  { scope: "admin:webhooks", group: "Webhooks", label: "Admin webhooks", desc: "Configure webhooks." },
  // Messaging
  { scope: "read:messaging",  group: "Messaging", label: "Read messaging", desc: "View messages between chats." },
  { scope: "write:messaging", group: "Messaging", label: "Write messaging", desc: "Send handoffs." },
  // Audit
  { scope: "read:audit", group: "Audit", label: "Read audit log", desc: "View team audit log." },
  // Pages (migration 037 — per-page nav gating)
  { scope: "read:overview",  group: "Pages", label: "Read overview",  desc: "View the Overview / activity-stream home page." },
  { scope: "read:chats",     group: "Pages", label: "Read chats",     desc: "View active AI chat sessions." },
  { scope: "read:files",     group: "Pages", label: "Read files",     desc: "View cross-chat file activity." },
  { scope: "read:analytics", group: "Pages", label: "Read analytics", desc: "View LLM spend + decision rate analytics." },
  { scope: "read:agents",    group: "Pages", label: "Read agents",    desc: "View per-session agent inspector." },
  // Account scopes (read:account, admin:account) retired in migration
  // 036 — the Account page is self-service and always accessible.
];

// Group ordering for the editor — matches the natural read→write→admin
// flow so the dropdown reads like a sentence.
const GROUP_ORDER = [
  "Activity", "Policy", "Settings", "Members",
  "Worktrees", "Billing", "Webhooks", "Messaging",
  "Audit", "Pages",
];

// Dev-time assertion: every SCOPE_VOCAB.group must appear in
// GROUP_ORDER, or that group's checkboxes get silently dropped by
// the filter at openRoleEditor (`GROUP_ORDER.filter(g => groups[g])`).
// We render but warn rather than throw so a single stale entry
// doesn't break the whole panel.
(function assertGroupsCovered() {
  const known = new Set(GROUP_ORDER);
  const missing = new Set();
  for (const e of SCOPE_VOCAB) {
    if (!known.has(e.group)) missing.add(e.group);
  }
  if (missing.size > 0) {
    console.warn(
      "[team-roles] SCOPE_VOCAB references groups not in GROUP_ORDER:",
      [...missing],
      "— their scopes won't appear in the editor.",
    );
  }
})();

// 5s cache mirror of team-bus.js. Gated on (tenantId, canManage) so
// multi-tenant switching + role-flip mid-session don't show stale
// admin buttons. Invalidated after every successful CRUD mutation.
let rolesCache = null;
let cacheTenant = null;
let cacheCanManage = null;
let lastFetch = 0;
const FETCH_TTL_MS = 5000;

// In-flight role being edited. null = no modal open. role_id = ""
// means "create new"; role_id set = "edit existing." The modal
// reads + writes this on Save.
let editingRole = null;

export async function renderRolesPanel(canManage) {
  const panel = $("team-roles-panel");
  const listEl = $("team-roles-list");
  const createBtn = $("team-role-create-btn");
  if (!panel || !listEl) return;

  if (createBtn) {
    createBtn.style.display = canManage ? "" : "none";
    // PR H (2026-05-15): same tier-aware badge as the Workspace
    // create button. Free-tier admins see "+ New role (Team)"
    // so they know upfront this is gated, not after the 402.
    //
    // PR H sweep finding #6 (LOW): gate the badge on teamState.info
    // being present so a Team-tier admin doesn't briefly see the
    // (Team) badge at boot before /v1/teams/info resolves.
    const tierKnown = !!(teamState.info && typeof teamState.info.tier === "string");
    const isFree = tierKnown && teamState.info.tier === "solo";
    if (isFree) {
      createBtn.innerHTML = `+ New role <span style="margin-left: 4px; font-size: 9px; padding: 1px 5px; border-radius: 999px; background: rgba(255,255,255,0.2); font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase;">Team</span>`;
      createBtn.title = "Custom roles are a Team feature. Click to upgrade.";
    } else {
      createBtn.textContent = "+ New role";
      createBtn.title = "";
    }
  }

  const tenantId = (teamState.info && teamState.info.team_id) || "";
  const cacheValid =
    rolesCache &&
    cacheTenant === tenantId &&
    cacheCanManage === canManage &&
    Date.now() - lastFetch < FETCH_TTL_MS;
  if (!cacheValid) {
    try {
      const resp = await apiWithMessage("/v1/teams/roles");
      rolesCache = Array.isArray(resp?.roles) ? resp.roles : [];
      cacheTenant = tenantId;
      cacheCanManage = canManage;
      lastFetch = Date.now();
    } catch (err) {
      const msg = (err && err.message) || "";
      // Suppress only the two expected unhappy paths (PR #126
      // pattern from team-bus.js): forbidden / not found means
      // the user isn't a member or the migration hasn't run.
      if (!/forbidden|not found|unauthorized/i.test(msg)) {
        console.warn("[team-roles] /v1/teams/roles failed:", err);
        showToast("error", "Couldn't load Roles: " + msg);
      }
      panel.style.display = "none";
      return;
    }
  }

  if (!rolesCache || rolesCache.length === 0) {
    panel.style.display = "none";
    return;
  }
  panel.style.display = "";

  // Render order: builtins first (Owner/Admin/Member/Viewer), then
  // custom roles alphabetically. Backend already returns this
  // order; we re-sort defensively.
  const sorted = [...rolesCache].sort((a, b) => {
    if (a.is_builtin && !b.is_builtin) return -1;
    if (!a.is_builtin && b.is_builtin) return 1;
    return a.name.localeCompare(b.name);
  });
  listEl.innerHTML = sorted.map((r) => renderRoleRow(r, canManage)).join("");

  // Per-row handlers — scoped to listEl for hygiene (sweep finding
  // from PR #126).
  wireRoleRowHandlers(canManage, listEl);
}

function renderRoleRow(role, canManage) {
  const scopeCount = Array.isArray(role.scopes) ? role.scopes.length : 0;
  // Standard roles render unbadged; custom roles get a "Custom" chip
  // so the standard/custom distinction is immediately visible.
  // Inverted from the original "Builtin"-on-standard convention —
  // standard is the common case and shouldn't need a marker.
  const customBadge = !role.is_builtin
    ? `<span style="font-size: 10px; padding: 2px 8px; border-radius: 999px; background: var(--accent-bg); color: var(--accent); font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; margin-left: 6px;">Custom</span>`
    : "";
  const editBtn = canManage
    ? `<button class="btn btn-sm role-row-edit" data-role-id="${escapeHTML(role.role_id)}" style="background: none; border: 1px solid var(--border); color: var(--text-secondary); font-size: 11px; padding: 4px 10px;">Edit</button>`
    : "";
  const deleteBtn = canManage && !role.is_builtin
    ? `<button class="btn btn-sm role-row-delete" data-role-id="${escapeHTML(role.role_id)}" data-role-name="${escapeHTML(role.name)}" style="background: none; border: 1px solid var(--danger); color: var(--danger); font-size: 11px; padding: 4px 10px;">Delete</button>`
    : "";
  // Show first 3 scopes inline + "+N more" for the rest, so the
  // row at-a-glance gives a sense of what the role grants.
  const scopePreview = (role.scopes || []).slice(0, 3).map((s) =>
    `<code style="font-size: 11px; padding: 1px 6px; border-radius: var(--r-sm); background: var(--bg-elevated); color: var(--text-secondary); margin-right: 4px;">${escapeHTML(s)}</code>`
  ).join("");
  const moreCount = scopeCount - 3;
  const moreHint = moreCount > 0
    ? `<span style="font-size: 11px; color: var(--text-tertiary);">+${moreCount} more</span>`
    : "";

  return `
    <div class="role-row" data-role-id="${escapeHTML(role.role_id)}" style="display: flex; align-items: center; justify-content: space-between; padding: var(--s3) var(--s5); border-bottom: 1px solid var(--border-subtle, var(--border));">
      <div style="display: flex; align-items: center; gap: var(--s3); flex: 1; min-width: 0;">
        <div style="min-width: 0; flex: 1;">
          <div style="display: flex; align-items: center;">
            <span style="font-weight: 500; color: var(--text); font-size: 13px;">${escapeHTML(role.name)}</span>
            ${customBadge}
          </div>
          <div style="margin-top: 4px;">${scopePreview}${moreHint}</div>
        </div>
      </div>
      <div style="display: flex; gap: var(--s2); align-items: center; flex-shrink: 0;">
        ${editBtn}
        ${deleteBtn}
      </div>
    </div>`;
}

function wireRoleRowHandlers(canManage, listEl) {
  listEl.querySelectorAll(".role-row-edit").forEach((btn) => {
    btn.addEventListener("click", (ev) => {
      const t = ev.currentTarget;
      const roleId = t.getAttribute("data-role-id") || "";
      const role = (rolesCache || []).find((r) => r.role_id === roleId);
      if (!role) return;
      openRoleEditor(role, canManage);
    });
  });
  listEl.querySelectorAll(".role-row-delete").forEach((btn) => {
    btn.addEventListener("click", async (ev) => {
      const t = ev.currentTarget;
      const roleId = t.getAttribute("data-role-id") || "";
      const roleName = t.getAttribute("data-role-name") || "";
      const ok = await showConfirm(
        `Delete role "${roleName}"?`,
        `Members holding this role will lose its scopes on their next request. The server refuses to delete a role that's currently assigned unless you supply a fallback — this UI doesn't yet support that flow, so reassign affected members first.`,
        "Delete",
        { destructive: true },
      );
      if (!ok) return;
      try {
        await apiWithMessage(`/v1/teams/roles/${encodeURIComponent(roleId)}`, {
          method: "DELETE",
        });
        showToast("success", `Deleted "${roleName}"`);
        rolesCache = null;
        invalidateTenantRolesCache();
        await renderRolesPanel(canManage);
      } catch (err) {
        // Auth-redirect: api.js already flipped to login. No toast.
        if (err instanceof AuthRedirectError) return;
        const rawMsg = (err && err.message) || "Failed to delete role";
        // The server says e.g. "role is assigned to N members; pass
        // fallback_role_id to reassign". The `fallback_role_id` half
        // is API jargon — this UI doesn't expose a reassign flow yet,
        // so users land on a toast that names a parameter they have
        // no way to set. Strip the API-name from the user-facing
        // toast; the meaning ("delete is blocked because members
        // still hold the role") survives.
        const friendly = rawMsg
          .replace(/;\s*pass\s+fallback_role_id\s+to\s+reassign\.?/i, "")
          .replace(/\bfallback_role_id\b/gi, "a fallback role");
        showToast("error", friendly);
      }
    });
  });
}

// openRoleEditor populates the modal. role=null means create-new
// (empty name, no scopes preselected); role=existing role means
// edit (preselect from role.scopes).
function openRoleEditor(role, canManage) {
  const backdrop = $("role-editor-backdrop");
  const titleEl = $("role-editor-title");
  const nameInput = $("role-editor-name");
  const nameHint = $("role-editor-name-hint");
  const scopesEl = $("role-editor-scopes");
  const errEl = $("role-editor-error");
  if (!backdrop || !titleEl || !nameInput || !scopesEl) return;

  editingRole = role; // null when creating
  const isBuiltin = !!(role && role.is_builtin);
  titleEl.textContent = role ? `Edit role "${role.name}"` : "New role";
  nameInput.value = role ? role.name : "";
  // Builtin names are immutable per the server's createRole/patchRole
  // contract — disable the input visually too.
  nameInput.disabled = isBuiltin;
  if (nameHint) nameHint.style.display = isBuiltin ? "" : "none";
  if (errEl) errEl.style.display = "none";

  // Build the grouped scope checklist. Pre-check anything currently
  // in role.scopes.
  const preselected = new Set((role && role.scopes) || []);
  const knownScopes = new Set(SCOPE_VOCAB.map((e) => e.scope));
  // Unknown scopes: present on the role but not in SCOPE_VOCAB.
  // Could be (a) a server-side scope the dashboard hasn't been
  // updated for yet, (b) the `*` full-access escape hatch, or
  // (c) a scope assigned via CLI / direct DB. Render as
  // preserved-but-disabled so round-trip Save doesn't drop them.
  const unknownScopes = [...preselected].filter((s) => !knownScopes.has(s));

  const groups = {};
  for (const entry of SCOPE_VOCAB) {
    if (!groups[entry.group]) groups[entry.group] = [];
    groups[entry.group].push(entry);
  }
  const groupHtml = GROUP_ORDER.filter((g) => groups[g]).map((g) => {
    const items = groups[g].map((entry) => {
      const checked = preselected.has(entry.scope) ? "checked" : "";
      const id = `role-scope-${entry.scope.replace(/:/g, "-")}`;
      return `
        <label for="${id}" style="display: flex; align-items: flex-start; gap: 8px; padding: 4px 0; cursor: pointer;">
          <input type="checkbox" id="${id}" data-scope="${escapeHTML(entry.scope)}" ${checked}
            style="margin-top: 3px;" ${canManage ? "" : "disabled"} />
          <div style="flex: 1; min-width: 0;">
            <div style="font-size: 12px; color: var(--text);">
              <code style="font-size: 11px; padding: 1px 6px; background: var(--bg-elevated); border-radius: var(--r-sm); margin-right: 4px;">${escapeHTML(entry.scope)}</code>
              ${escapeHTML(entry.label)}
            </div>
            <div style="font-size: 11px; color: var(--text-tertiary); margin-top: 2px;">${escapeHTML(entry.desc)}</div>
          </div>
        </label>`;
    }).join("");
    return `
      <details open style="margin-bottom: var(--s3);">
        <summary style="font-size: 11px; color: var(--text-secondary); text-transform: uppercase; letter-spacing: 0.05em; cursor: pointer; padding: 4px 0;">${escapeHTML(g)}</summary>
        <div style="padding-left: var(--s3);">${items}</div>
      </details>`;
  }).join("");

  // Unknown-scopes section. Rendered ABOVE the standard checklist
  // so it's the first thing the editor sees on a role with
  // out-of-band scopes. Checkboxes are checked-and-disabled — the
  // Save handler reads them via the `data-scope` attribute the
  // same way it reads known scopes, so they ride through unchanged.
  // The `*` escape hatch is the most likely real-world hit here.
  const unknownHtml = unknownScopes.length === 0 ? "" : `
    <details open style="margin-bottom: var(--s3); border: 1px solid var(--warn, var(--border)); border-radius: var(--r-sm); padding: 6px 10px;">
      <summary style="font-size: 11px; color: var(--text-secondary); text-transform: uppercase; letter-spacing: 0.05em; cursor: pointer; padding: 4px 0;">Unknown to this UI (preserved on save)</summary>
      <div style="padding-left: var(--s3);">
        ${unknownScopes.map((s) => {
          const id = `role-scope-${s.replace(/:/g, "-").replace(/\*/g, "star")}`;
          return `
            <label for="${id}" style="display: flex; align-items: flex-start; gap: 8px; padding: 4px 0;">
              <input type="checkbox" id="${id}" data-scope="${escapeHTML(s)}" checked disabled style="margin-top: 3px;" />
              <div style="flex: 1; min-width: 0;">
                <div style="font-size: 12px; color: var(--text);">
                  <code style="font-size: 11px; padding: 1px 6px; background: var(--bg-elevated); border-radius: var(--r-sm); margin-right: 4px;">${escapeHTML(s)}</code>
                </div>
                <div style="font-size: 11px; color: var(--text-tertiary); margin-top: 2px;">
                  Granted out-of-band or by a newer server version. Kept as-is when you save.
                </div>
              </div>
            </label>`;
        }).join("")}
      </div>
    </details>`;
  scopesEl.innerHTML = unknownHtml + groupHtml;

  // .confirm-backdrop is hidden via display:none (default) and
  // shown via `.open` adding display:flex. No inline style toggle
  // needed — match the rest of the dashboard's modal pattern.
  backdrop.classList.add("open");
  setTimeout(() => nameInput.focus(), 50);
}

function closeRoleEditor() {
  const backdrop = $("role-editor-backdrop");
  if (!backdrop) return;
  backdrop.classList.remove("open");
  editingRole = null;
}

// wireRoleEditor attaches the Save / Cancel handlers + the
// backdrop-click-closes behavior. Idempotent: a second call no-ops.
let editorWired = false;
export function wireRoleEditor(getCanManage) {
  if (editorWired) return;
  const backdrop = $("role-editor-backdrop");
  const cancelBtn = $("role-editor-cancel");
  const saveBtn = $("role-editor-save");
  const nameInput = $("role-editor-name");
  const errEl = $("role-editor-error");
  const createBtn = $("team-role-create-btn");
  if (!backdrop || !cancelBtn || !saveBtn || !nameInput) return;
  editorWired = true;

  // Cancel + backdrop-click both close.
  cancelBtn.addEventListener("click", closeRoleEditor);
  backdrop.addEventListener("click", (ev) => {
    if (ev.target === backdrop) closeRoleEditor();
  });

  // "+ New role" button opens the editor in create-mode.
  if (createBtn) {
    createBtn.addEventListener("click", () => {
      // getCanManage is a closure into team-page.js's isAdmin||isOwner
      // resolution. Pass through so the editor knows whether to
      // disable inputs (defense-in-depth — the button itself is
      // already hidden for non-admins).
      const canManage = typeof getCanManage === "function" ? getCanManage() : false;
      openRoleEditor(null, canManage);
    });
  }

  // Submit on Enter inside the name field.
  nameInput.addEventListener("keydown", (ev) => {
    if (ev.key === "Enter") {
      ev.preventDefault();
      saveBtn.click();
    }
  });

  saveBtn.addEventListener("click", async () => {
    // Snapshot editingRole at handler entry. closeRoleEditor()
    // nulls module-scope `editingRole`, so if the user clicks
    // Cancel / backdrop during the awaited apiWithMessage we'd
    // dereference null below (TypeError on `.is_builtin` /
    // `.role_id` / `.name`). Reading once up-front means the in-
    // flight save uses a stable view of what we were editing.
    const currentRole = editingRole;
    const isBuiltin = !!(currentRole && currentRole.is_builtin);
    const name = (nameInput.value || "").trim();
    const scopesEl = $("role-editor-scopes");
    // querySelectorAll(':checked') matches disabled+checked boxes
    // too — that's how preserved unknown scopes survive Save.
    const checked = scopesEl
      ? Array.from(scopesEl.querySelectorAll('input[type="checkbox"]:checked'))
      : [];
    const scopes = checked.map((el) => el.getAttribute("data-scope") || "").filter(Boolean);

    if (errEl) errEl.style.display = "none";

    const canManage = typeof getCanManage === "function" ? getCanManage() : false;

    saveBtn.disabled = true;
    try {
      if (!currentRole) {
        // Create
        if (!name) throw new Error("Name is required");
        await apiWithMessage("/v1/teams/roles", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name, scopes }),
        });
        showToast("success", `Created "${name}"`);
      } else {
        // Edit. PATCH accepts a partial body — we always send both
        // name + scopes since the user might have changed either.
        // Server refuses name-edit on builtins, which manifests as
        // a 400; we pre-disable the input but defensive against
        // dev-tools tampering.
        const body = isBuiltin ? { scopes } : { name, scopes };
        await apiWithMessage(`/v1/teams/roles/${encodeURIComponent(currentRole.role_id)}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        showToast("success", `Saved "${isBuiltin ? currentRole.name : name}"`);
      }
      closeRoleEditor();
      rolesCache = null;
      invalidateTenantRolesCache();
      await renderRolesPanel(canManage);
    } catch (err) {
      // Auth-redirect: api.js already flipped to login. No toast.
      if (err instanceof AuthRedirectError) return;
      // PR D: 402 upgrade_required → upgrade modal instead of toast.
      // Only fires on create (POST /v1/teams/roles) — PATCH on
      // builtins won't trip the tier gate.
      if (err && err.upgradeRequired) {
        // Close the role editor before showing the upgrade modal so
        // the user isn't looking at two stacked modals.
        closeRoleEditor();
        // PR G (2026-05-15): save the pending role config so it
        // auto-creates after the Stripe round-trip. Only on
        // create (currentRole === null) — PATCH wouldn't have
        // tripped the gate.
        if (!currentRole) {
          saveCheckoutResume("role", { name, scopes });
        }
        await showUpgradeRequiredFromError(err);
        return;
      }
      const msg = (err && err.message) || "Failed to save role";
      if (errEl) {
        errEl.textContent = msg;
        errEl.style.display = "";
      }
    } finally {
      saveBtn.disabled = false;
    }
  });
}

// Invalidate cache from outside (e.g. after role-affecting member
// changes that might rotate role_ids). Defensive; not currently
// triggered.
export function invalidateRolesCache() {
  rolesCache = null;
}
